/** Timesheets — lifecycle per employee and pay period (spec §4.5, §4.4 monthly rhythm, D8). */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RefreshCw, CheckCheck, Calculator, Sheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ReasonDialog from './ReasonDialog';
import { TS_STATUS, api, employeeName, errorText, hm, label, thisMonth } from './shared';

export default function TimesheetsTab({ token, isRTL, access, go }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [month, setMonth] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);     // {row, step} or {bulk: true}
  const [recalculating, setRecalculating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await api(token).get('/timesheets', { month });
      setRows(r.items); setPeriod(r.period);
    } catch (e) { setError(errorText(e)); }
    finally { setLoading(false); }
  }, [token, month]);
  useEffect(() => { load(); }, [load]);

  const run = async (reason) => {
    try {
      if (pending.bulk) {
        const r = await api(token).post('/timesheets/bulk-approve', {}, { month });
        toast.success(t(`${r.approved.length} approved, ${r.skipped.length} need answers first`,
          `اعتُمد ${r.approved.length}، و${r.skipped.length} تحتاج إجابات أولاً`));
      } else {
        await api(token).post(`/timesheets/${pending.row.employee.id}/${month}/${pending.step}`, { reason: reason || null });
        toast.success(t('Done', 'تم'));
      }
      setPending(null); load();
    } catch (e) { toast.error(errorText(e)); }
  };

  // Open questions are counted from the stored days: recalculate the month after rules, rosters or punches changed.
  const recalculate = async () => {
    setRecalculating(true);
    try {
      const r = await api(token).post('/recompute', {}, { month });
      toast.success(t(`${r.employees} employees recalculated, ${r.open_suggestions} open questions`,
        `أعيد حساب ${r.employees} موظف، ${r.open_suggestions} سؤال مفتوح`));
      load();
    } catch (e) { toast.error(errorText(e)); } finally { setRecalculating(false); }
  };

  // Order (Excel approval line): direct manager → HR → employee → final → pay released.
  const steps = (row) => {
    const s = row.status;
    const out = [];
    if (row.i_am_direct_manager) {
      if (['draft', 'returned'].includes(s)) out.push(['manager-approve', t('Approve (manager)', 'اعتماد (المدير)')]);
      if (['draft', 'manager_approved'].includes(s)) out.push(['manager-return', t('Return to HR', 'إعادة للموارد البشرية')]);
    }
    if (!access?.hr_scope) return out;
    if (['draft', 'returned'].includes(s) && !row.manager_can_sign && !row.i_am_direct_manager) {
      out.push(['manager-approve', t('Approve for the missing manager', 'اعتماد نيابة عن المدير غير المحدد')]);
    }
    if (s === 'manager_approved' || (s === 'disputed' && !row.open_disputes)) out.push(['approve', t('Approve (HR)', 'اعتماد (الموارد البشرية)')]);
    if (s === 'employee_confirmed') out.push(['finalise', t('Finalise', 'إقفال')]);
    if (s === 'final' && access?.can_release) out.push(['release', t('Release pay', 'إطلاق الراتب')]);
    if (['final', 'released'].includes(s) && access?.can_release) out.push(['reopen', t('Reopen', 'إعادة فتح')]);
    return out;
  };

  return (
    <div className="space-y-4 mt-4" data-testid="attendance-timesheets">
      <div className="flex flex-wrap items-center gap-2">
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="ts-month" />
        {period && (
          <span className="text-sm text-slate-600" data-testid="ts-period">
            {t('Pay period', 'فترة الراتب')} {period.start} – {period.end}
            <span className="text-xs text-amber-700 ms-1">({t('to confirm with payroll', 'بانتظار تأكيد الرواتب')})</span>
          </span>
        )}
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <Button variant="outline" size="sm" onClick={recalculate} disabled={recalculating} data-testid="ts-recalculate">
          {recalculating ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Calculator className="h-4 w-4 me-1" />}
          {t('Recalculate month', 'إعادة حساب الشهر')}
        </Button>
        {access?.hr_scope && (
          <Button size="sm" className="ms-auto" onClick={() => setPending({ bulk: true })} data-testid="ts-bulk-approve">
            <CheckCheck className="h-4 w-4 me-1" />{t('Approve all the managers approved', 'اعتماد كل ما اعتمده المدراء')}
          </Button>
        )}
      </div>
      {error && <div className="text-sm text-rose-600">{error}</div>}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('Employee', 'الموظف')}</TableHead>
            <TableHead>{t('Status', 'الحالة')}</TableHead>
            <TableHead>{t('Direct manager', 'المدير المباشر')}</TableHead>
            <TableHead>{t('Open questions', 'أسئلة مفتوحة')}</TableHead>
            <TableHead>{t('Disputes', 'اعتراضات')}</TableHead>
            <TableHead>{t('Overtime (approved)', 'الإضافي (المعتمد)')}</TableHead>
            <TableHead>{t('Actions', 'إجراءات')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.employee.id} data-testid="ts-row">
              <TableCell>
                <button className="hover:underline" onClick={() => go('employee', { employee: r.employee.id, month })}>
                  {employeeName(r.employee, isRTL)}
                </button>
              </TableCell>
              <TableCell><span className={`text-xs rounded-full px-2 py-0.5 ${TS_STATUS[r.status]?.cls || ''}`}>{label(TS_STATUS, r.status, isRTL)}</span></TableCell>
              <TableCell className="text-xs">
                {r.manager_can_sign ? <span className="text-slate-500">{r.i_am_direct_manager ? t('you', 'أنت') : t('on file', 'محدد')}</span>
                  : <span className="text-amber-700" title={r.manager_missing}>— {t('missing in profile', 'غير موجود في الملف')}</span>}
              </TableCell>
              <TableCell className={r.open_questions ? 'text-amber-700 font-semibold' : ''}>{r.open_questions}</TableCell>
              <TableCell className={r.open_disputes ? 'text-amber-700 font-semibold' : ''}>{r.open_disputes}</TableCell>
              <TableCell>{r.summary ? hm(r.summary.overtime_minutes) : '—'}</TableCell>
              <TableCell className="space-x-1 rtl:space-x-reverse">
                <Button size="sm" variant="ghost" onClick={() => go('sheet', { employee: r.employee.id, month })} data-testid="ts-sheet">
                  <Sheet className="h-4 w-4 me-1" />{t('Sheet', 'الكشف')}
                </Button>
                {steps(r).map(([step, text]) => (
                  <Button key={step} size="sm" variant={['reopen', 'manager-return'].includes(step) ? 'outline' : 'default'} disabled={['approve', 'manager-approve'].includes(step) && r.open_questions > 0}
                    onClick={() => setPending({ row: r, step })} data-testid={`ts-${step}`}>{text}</Button>
                ))}
              </TableCell>
            </TableRow>
          ))}
          {!loading && !rows.length && (
            <TableRow><TableCell colSpan={7} className="text-center text-slate-500">{t('No employees.', 'لا يوجد موظفون.')}</TableCell></TableRow>
          )}
        </TableBody>
      </Table>
      <p className="text-xs text-slate-500">
        {t('Order: the direct manager approves (or returns with a reason), then HR approves; the employee then sees the period and has 3 days to confirm or raise a Timesheet review request; then HR finalises. When the profile has no direct manager who can sign, HR approves in the manager’s place with a reason. Pay amounts reach the employee only after Release.',
          'الترتيب: يعتمد المدير المباشر (أو يعيده مع السبب)، ثم تعتمد الموارد البشرية؛ بعدها يرى الموظف الفترة ولديه 3 أيام لتأكيدها أو رفع طلب مراجعة كشف الدوام؛ ثم الاعتماد النهائي. إذا لم يكن في الملف مدير مباشر يمكنه الاعتماد، تعتمد الموارد البشرية نيابةً عنه مع ذكر السبب. لا يرى الموظف مبالغ الراتب إلا بعد الإطلاق.')}
      </p>
      <ReasonDialog open={!!pending} isRTL={isRTL}
        optional={!(pending?.step === 'reopen' || pending?.step === 'manager-return'
          || (pending?.step === 'manager-approve' && !pending?.row?.i_am_direct_manager))}
        destructive={['reopen', 'manager-return'].includes(pending?.step)}
        title={pending?.bulk ? t('Approve all the managers approved', 'اعتماد كل ما اعتمده المدراء')
          : pending ? `${steps(pending.row).find(([s]) => s === pending.step)?.[1] || ''} — ${employeeName(pending.row.employee, isRTL)}` : ''}
        description={pending?.step === 'reopen' ? t('Reopening unlocks the month; a reason is required and recorded.', 'إعادة الفتح تفك قفل الشهر؛ السبب مطلوب ويُسجَّل.')
          : pending?.step === 'manager-return' ? t('The timesheet goes back to HR with your reason.', 'يعود الكشف إلى الموارد البشرية مع سببك.')
          : pending?.step === 'manager-approve' && !pending?.row?.i_am_direct_manager
            ? t('This employee has no direct manager who can sign. Your approval is recorded as “HR on behalf of the manager”; a reason is required.',
              'لا يوجد لهذا الموظف مدير مباشر يمكنه الاعتماد. يُسجَّل اعتمادك «الموارد البشرية نيابة عن المدير»؛ السبب مطلوب.')
            : undefined}
        onCancel={() => setPending(null)} onConfirm={run} />
    </div>
  );
}
